import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import useDocumentTitle from '../hooks/useDocumentTitle';

type Status = 'loading' | 'success' | 'error';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useDocumentTitle('Cognidaily | Verify Email');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Verification link is missing. Please check your email for the correct link.');
      return;
    }

    const verify = async () => {
      try {
        const response = await axios.get('/api/auth/verify-email', {
          params: { token }
        });

        if (response.data?.success) {
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage(
            response.data?.message || 'Verification failed. Please try again.'
          );
        }
      } catch (err: any) {
        setStatus('error');
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          'This verification link is invalid or has expired. Please register again or contact support if you need help.';
        setErrorMessage(message);
      }
    };

    verify();
  }, [token]);

  return (
    <section className="section section--auth">
      <h1>Email verification</h1>

      {status === 'loading' && (
        <>
          <p>Verifying your email address…</p>
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </>
      )}

      {status === 'success' && (
        <>
          <p className="text-success" role="status">
            <strong>Your email has been verified.</strong>
          </p>
          <p>
            Your account is now active. You can sign in and start playing.
          </p>
          <p>
            <Link to="/login" className="btn btn-primary">
              Sign in
            </Link>
          </p>
        </>
      )}

      {status === 'error' && (
        <>
          <p role="alert" className="text-error">
            {errorMessage}
          </p>
          <p>
            <Link to="/login" className="link">Back to sign in</Link>
            {' · '}
            <Link to="/register" className="link">Register again</Link>
          </p>
        </>
      )}
    </section>
  );
};

export default VerifyEmail;
